// Idle timer hook for showcase mode activation

import { useState, useEffect, useCallback, useRef } from 'react';

interface UseIdleTimerOptions {
  timeout: number; // milliseconds
  onIdle?: () => void;
  onActive?: () => void;
  events?: string[];
}

interface UseIdleTimerResult {
  isIdle: boolean;
  reset: () => void;
  pause: () => void;
  resume: () => void;
  getRemainingTime: () => number;
}

const DEFAULT_EVENTS = [
  'mousemove',
  'mousedown',
  'touchstart',
  'touchmove',
  'keydown',
  'scroll',
  'wheel',
];

export function useIdleTimer(options: UseIdleTimerOptions): UseIdleTimerResult {
  const {
    timeout,
    onIdle,
    onActive,
    events = DEFAULT_EVENTS,
  } = options;

  const [isIdle, setIsIdle] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActiveRef = useRef<number>(Date.now());

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    lastActiveRef.current = Date.now();

    if (!isPaused) {
      timerRef.current = setTimeout(() => {
        setIsIdle(true);
        onIdle?.();
      }, timeout);
    }
  }, [timeout, onIdle, isPaused]);

  const handleActivity = useCallback(() => {
    if (isIdle) {
      setIsIdle(false);
      onActive?.();
    }
    resetTimer();
  }, [isIdle, onActive, resetTimer]);

  useEffect(() => {
    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    resetTimer();

    return () => {
      // Cleanup
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [events, handleActivity, resetTimer]);

  const reset = useCallback(() => {
    setIsIdle(false);
    resetTimer();
  }, [resetTimer]);

  const pause = useCallback(() => {
    setIsPaused(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
    resetTimer();
  }, [resetTimer]);

  const getRemainingTime = useCallback(() => {
    if (isIdle || isPaused) return 0;
    const elapsed = Date.now() - lastActiveRef.current;
    return Math.max(0, timeout - elapsed);
  }, [timeout, isIdle, isPaused]);

  return {
    isIdle,
    reset,
    pause,
    resume,
    getRemainingTime,
  };
}
